/**
 * Защита от загрязнения прототипа при разборе файлов.
 *
 * Библиотека чтения таблиц исторически подвержена этой уязвимости:
 * специально созданный файл может внедрить свойства в базовый объект,
 * от которого наследуются все остальные. Последствия — от неверной
 * работы приложения до выполнения чужого кода.
 *
 * Исправленная версия библиотеки распространяется только с сайта
 * разработчика и в закрытом контуре может оказаться недоступна.
 * Поэтому защита продублирована здесь: даже с уязвимой версией
 * опасные ключи не попадут в данные.
 */

/** Ключи, через которые выполняется загрязнение прототипа. */
const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Очищает разобранные данные от опасных ключей.
 *
 * Обходит структуру целиком: вложенность в файлах таблиц бывает
 * неглубокой, но полагаться на это нельзя.
 *
 * @param {*} value — результат разбора файла
 * @returns {*} те же данные без опасных ключей
 */
export function sanitizeParsed(value, depth = 0) {
  /* Ограничение глубины — защита от специально созданной структуры
     с бесконечной вложенностью, которая иначе исчерпала бы стек. */
  if (depth > 20) return null;

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeParsed(item, depth + 1));
  }

  if (value === null || typeof value !== 'object') return value;

  /* Объект создаётся без прототипа: даже если опасный ключ каким-то
     образом просочится, наследования от базового объекта не будет. */
  const clean = Object.create(null);

  for (const key of Object.keys(value)) {
    if (DANGEROUS_KEYS.has(key)) continue;
    clean[key] = sanitizeParsed(value[key], depth + 1);
  }

  /* Возвращаем обычный объект: с ним привычно работает остальной код,
     а опасные ключи уже отброшены. */
  return Object.assign({}, clean);
}

/**
 * Проверяет размер файла перед разбором.
 *
 * Слишком большой файл занимает память браузера целиком и подвешивает
 * вкладку. Пятнадцати мегабайт достаточно с запасом: реальные выгрузки
 * по всей сети занимают меньше мегабайта.
 */
export const MAX_FILE_SIZE = 15 * 1024 * 1024;

export function checkFileSize(file) {
  if (file.size > MAX_FILE_SIZE) {
    const limit = Math.round(MAX_FILE_SIZE / 1024 / 1024);
    return `Файл слишком велик (${Math.round(file.size / 1024 / 1024)} МБ). Допустимо до ${limit} МБ.`;
  }
  return null;
}
